// ── Shared presentational + data-shaping helpers for node drawers ───────────
//
// Every sub-drawer edits `task.configuration.parameters` (a loose
// `Record<string, unknown>` mock bag) through the same immutable
// read/patch pattern, and renders the same compact dark-console form
// controls. Centralising both here keeps each drawer file focused on its
// own field list rather than re-deriving input styling or parameter
// plumbing every time.
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import type { StepTask } from '../../types'

const inputClass =
  'w-full bg-[#0b1220] border border-[#1e2c44] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] ' +
  'placeholder:text-[#5b6b85] focus:outline-none focus:border-[#818cf8]/60 disabled:opacity-50'

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[10px] uppercase tracking-wide text-[#5b6b85] mb-1.5">{children}</p>
}

// A labelled section divider used to separate the config parsed from the
// workflow JSON (top) from the mockup-only "ideal ops center" config (below).
export function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-primary)] shrink-0">
        {title}
      </span>
      {hint && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#1e2c44] text-[#8fa0bd] shrink-0">{hint}</span>
      )}
      <span className="flex-1 h-px bg-[#1e2c44]" />
    </div>
  )
}

// A read-only value display for config parsed from the workflow JSON.
export function ReadonlyValue({ children }: { children: ReactNode }) {
  return (
    <div className="w-full bg-[#0b1220] border border-[#1e2c44] rounded-md px-2.5 py-1.5 text-xs text-[var(--text-primary)] break-words">
      {children}
    </div>
  )
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  )
}

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputClass} ${className ?? ''}`} />
}

export function SelectInput({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={`${inputClass} ${className ?? ''}`}>
      {children}
    </select>
  )
}

export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs text-[var(--text-primary)] cursor-pointer py-1">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="relative w-8 h-4 rounded-full transition-colors shrink-0"
        style={{ background: checked ? '#6366f1' : '#1e2c44' }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform"
          style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }}
        />
      </button>
    </label>
  )
}

export function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-[var(--text-primary)] cursor-pointer py-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[#818cf8] w-3.5 h-3.5 shrink-0"
      />
      <span className="truncate">{label}</span>
    </label>
  )
}

// ── Task parameter plumbing ──
// Every StepTask stores its mock config under `configuration.parameters`.
// These two helpers give drawers a uniform, immutable way to read a field
// out of that bag and to write a single key back into it.
export function getParams(task: StepTask): Record<string, unknown> {
  return task.configuration.parameters ?? {}
}

export function setParam(
  onChange: (updater: (t: StepTask) => StepTask) => void,
  key: string,
  value: unknown,
): void {
  onChange((t) => ({
    ...t,
    configuration: { ...t.configuration, parameters: { ...(t.configuration.parameters ?? {}), [key]: value } },
  }))
}
